import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Group,
  Image,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { formatBestBefore, formatPrice, isFree } from "@livecommerce/ui";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import {
  type AuctionSettings,
  type Lot,
  type LotAction,
  type LotState,
  type ShowState,
  type StockCheck,
  shows,
} from "./api";
import { AuctionFields } from "./AuctionFields";
import { priceProblem } from "./price";
import {
  SELL_SECONDS_MAX,
  SELL_SECONDS_MIN,
  secondsUntil,
  type SellInput,
  sellSecondsInvalid,
  sellSecondsOf,
} from "./sale";
import { FeatureRefusal, type Refusal } from "./FeatureRefusal";
import { auctionChanged, DEFAULT_AUCTION } from "./auction";
import styles from "./Shows.module.scss";

/**
 * One lot in the running order (`R-HOST-14`, `R-HOST-15`, spec §7.3).
 *
 * ## The controls change with the lot's state, and that is the point
 *
 * `R-HOST-15` calls feature, open and close the most frequent actions in the product and asks
 * that they be reachable without navigating or looking away — so exactly one of them is offered
 * at a time, in the same place, as the primary button on the row. There is nothing to choose
 * between and nothing to read: the next thing to do to this lot is the button that is there.
 *
 * ## Editing stops at `featured`, not at `open`
 *
 * `R-HOST-18b`. The price and position inputs disappear the moment the lot is featured, because
 * from that moment a customer is looking at it. The allocation input stays, because `R-INV-6` and
 * `R-HOST-18` are explicit that a popular lot can be given more mid-show — that changes what is
 * left, not what is being bought.
 *
 * ## The auction's settings stop one state later (M1.1)
 *
 * `R-AUC-8` — *changeable up until the lot opens.* So the four auction inputs survive into
 * `featured`, where the price input has already gone. That is not an inconsistency: a featured
 * auction lot has no bid control on anybody's screen, so changing its duration changes the terms
 * of nothing that has happened, whereas a featured fixed-price lot has a claim button and its
 * price is a number somebody is about to pay. The rule in one sentence, which is what the panel
 * has to make legible: *the auction's shape can change until it opens; its starting price cannot
 * change once anybody has seen it.*
 *
 * The panel hiding a control is not the enforcement. Every one of these rules is a `where` clause
 * in Postgres (`packages/core/src/shows/lots.ts`); this is only what stops a host being offered
 * something that would be refused.
 */
export function LotRow({
  showId,
  showState,
  lot,
  controls,
  anotherOpen,
  onChanged,
}: {
  showId: string;
  showState: ShowState;
  lot: Lot;
  /**
   * Whether this row can be acted on, or is only shown (spec §3).
   *
   * False for a **moderator**, who reads the running order from beside the comments feed and
   * drives none of it. Every control goes — the edit fields, the auction
   * settings, and the feature/open/close button that is the row's whole point — leaving the
   * line of text: what the lot is, what state it is in, and how much of it is left.
   *
   * Withheld rather than disabled, for the reason `ShowPage` gives, and not the enforcement
   * either: every route behind these answers 403 for a moderator (`requireHost`).
   */
  controls: boolean;
  /** Another lot in this show is open, so this one cannot be featured (`featureLot`). */
  anotherOpen: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  /**
   * **Which** action is in flight, not merely whether one is.
   *
   * A single boolean here would put a spinner in every button on the row at once, so pressing
   * *Open* would look as though Withdraw, Close and Save had all been pressed too. On a control surface whose
   * whole job is to make the next action obvious (`R-HOST-15`), that is worse than it sounds: the
   * one thing the operator needs to see is which of them they just hit.
   */
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [refusal, setRefusal] = useState<Refusal | undefined>(undefined);
  const [price, setPrice] = useState(lot.price);
  const [allocated, setAllocated] = useState(lot.allocated);
  /** `R-LOT-34` — the sale's time limit, in seconds; empty for none. */
  const [sellSeconds, setSellSeconds] = useState<SellInput>(lot.sellSeconds ?? "");
  const sellChanged = sellSecondsOf(sellSeconds) !== lot.sellSeconds;
  const sellInvalid = sellSecondsInvalid(sellSeconds);
  const [saleMode, setSaleMode] = useState(lot.saleMode);
  // `R-LOT-32` — judged only once something has changed, so a row is never red for a price saved
  // before the rule existed until somebody touches it. The server refuses it at publish anyway.
  // A free lot says so, as it does in the room (`R-LOT-32`).
  const lotPrice = (value: string) =>
    isFree(value) ? t("lots.free") : formatPrice(value);
  const problem =
    price !== lot.price || saleMode !== lot.saleMode
      ? priceProblem(price, saleMode)
      : undefined;
  const [auction, setAuction] = useState<AuctionSettings>(
    lot.auction ?? DEFAULT_AUCTION,
  );

  // `controls` leads every one of these, so a moderator's row has no editable state at all
  // rather than a set of flags that happen to be false everywhere it is checked.
  const editable = controls && lot.state === "queued";
  const running = controls && showState === "live";
  /** `R-AUC-8` — the boundary is the open, one state past the price's. */
  const auctionEditable =
    controls &&
    saleMode === "auction" &&
    ["queued", "featured"].includes(lot.state) &&
    !["ended", "cancelled"].includes(showState);
  /** Whether anything in the auction block differs from what the row was handed. */
  const changed = auctionChanged(auction, lot.auction);

  async function run(key: string, work: () => Promise<unknown>) {
    setBusy(key);
    setError(undefined);
    try {
      await work();
    } catch (failure) {
      // A refused feature is said in a modal, with the stock it was decided on — see
      // `FeatureRefusal` for why a line on the row was not enough.
      if ((key === "feature" || key === "again") && failure instanceof ApiError) {
        setRefusal({
          message: failure.message,
          stock: failure.body.stock as StockCheck | undefined,
        });
      } else {
        setError(
          failure instanceof ApiError ? failure.message : t("shows.unreachable"),
        );
      }
    } finally {
      setBusy(undefined);
    }

    /**
     * Outside the `try`, and always, which is two decisions.
     *
     * **Outside**, so that a reload failing is never reported as the action failing. Inside, a
     * successful open followed by an unreachable API would tell the operator their lot did not
     * open — when it did, and is now selling.
     *
     * **Always**, including after a failure, because the most common failure here is losing a
     * race to somebody else running the same show (`R-HOST-31`) and the refreshed state is the
     * explanation. The error is local to this row and survives the refresh, so both are visible:
     * what went wrong, and what the lot looks like now.
     */
    await onChanged();
  }

  /** Anything in flight disables the rest of the row; only the one pressed spins. */
  const working = busy !== undefined;

  const image = lot.snapshot?.images[0];
  /**
   * A shortlisted lot nobody priced takes Shopify's price when it is featured, so the row shows
   * Shopify's price **as last heard** rather than the one captured when it was shortlisted.
   */
  const shownPrice =
    lot.state === "queued" && lot.priceFollowsShopify && lot.stock
      ? lot.stock.price
      : lot.price;

  return (
    <Paper withBorder radius="md" p="sm" className={styles.card}>
      <FeatureRefusal refusal={refusal} onClose={() => setRefusal(undefined)} />
      <Stack gap="xs">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          {image ? (
            <Image
              src={image.url}
              alt={image.alt ?? ""}
              w={44}
              h={44}
              radius="sm"
              fit="cover"
            />
          ) : null}

          <Stack gap={2} className={styles.grow}>
            <Group gap="xs">
              <Text size="sm" fw={600}>
                {lot.snapshot?.variantTitle
                  ? `${lot.snapshot.productTitle} — ${lot.snapshot.variantTitle}`
                  : (lot.snapshot?.productTitle ?? lot.variantId)}
              </Text>
              {/* No badge for *Queued* — every lot on the shortlist is — or for *Unsold*, which is
                  under *Done* already and read as a problem where there is none (M1.13). */}
              {lot.state === "unsold" || lot.state === "queued" ? null : (
                <LotStateBadge state={lot.state} />
              )}
              {/*
                How the lot sells, so auctions and fixed-price lots can be told apart at a glance
                down any list — the shortlist, on air, done, and an ended show's whole list, where
                every lot is sold or unsold rather than queued. Filled rather than light: the brand
                yellow is only legible as a fill, where the theme's `autoContrast` puts ink on it.
              */}
              <Badge
                size="xs"
                variant="filled"
                color={lot.saleMode === "auction" ? "peeko" : "blue"}
                radius="sm"
              >
                {t(
                  lot.saleMode === "auction" ? "lots.auctionBadge" : "lots.fixedBadge",
                )}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {/*
                Sold as well as allocated and left, because *how many went* is the figure a host
                is asked out loud and the row could only imply it — `allocated - remaining`,
                worked out in somebody's head while they are presenting.
              */}
              {/*
                A shortlisted lot with no quantity has none until it is featured (M1.13): the
                feature decides it from Shopify's stock at that moment.
              */}
              {/*
                On the shortlist the row says what is **in stock** — what the operator is choosing
                against — rather than a quantity, which is only decided when the lot is featured.
              */}
              {lot.state === "queued"
                ? lot.stock?.available != null
                  ? t("lots.rowMetaStock", {
                      price: lotPrice(shownPrice),
                      count: lot.stock.available,
                    })
                  : t("lots.rowMetaNoStock", { price: lotPrice(shownPrice) })
                : t("lots.rowMeta", {
                    price: lotPrice(lot.price),
                    allocated: lot.allocated,
                    sold: lot.consumed,
                    remaining: lot.remaining,
                  })}
              {/* Shopify's compare-at, inline with the price it is a comparison to.
                  Orange when it is *not* above the show price: Shopify permits that, it is a
                  merchandising mistake rather than a discount, and the room withholds it rather
                  than claiming a false saving. The colour is the only trace of that here — the
                  operator can fix it in Shopify, and nobody else can. */}
              {lot.snapshot?.compareAtPrice ? (
                <Text
                  component="span"
                  size="xs"
                  c={
                    Number(lot.snapshot.compareAtPrice) > Number(lot.price)
                      ? "dimmed"
                      : "orange"
                  }
                  title={
                    Number(lot.snapshot.compareAtPrice) > Number(lot.price)
                      ? undefined
                      : t("lots.rrpNotShown")
                  }
                >
                  {" · "}
                  {t("lots.rrp", { was: formatPrice(lot.snapshot.compareAtPrice) })}
                </Text>
              ) : null}
              {/*
                The break-even bid (`R-LOT-31`), beside the price it is a margin against — an
                operator pricing a lot is comparing the two, and making them look up the second
                one in Shopify is how a lot gets priced below cost. It is the cost *plus VAT*
                where Shopify charges it, because the price is VAT-inclusive and the cost is not —
                and the cost itself where it does not. Bold, label and figure both: it is the number
                on the row an operator is pricing against.

                **This is the only surface it appears on.** The customer application has no field
                for it; see `publicLotJson`.

                Said explicitly when Shopify holds none, rather than shown as nothing: a blank
                would read as free, and *no cost set* is a thing an operator can go and fix.
              */}
              {lot.snapshot ? (
                <Text component="span" size="xs" c="dimmed">
                  {" · "}
                  {lot.snapshot.breakEven ? (
                    <Text component="span" size="xs" fw={700}>
                      {t("lots.breakEven", {
                        amount: formatPrice(lot.snapshot.breakEven),
                      })}
                    </Text>
                  ) : (
                    t("lots.costNone")
                  )}
                </Text>
              ) : null}
              {/*
                `R-LOT-30` — the date the room shows on the lot card, in the same `DD/MM/YY`, so an
                operator can see what viewers will be told before featuring it. Left out where
                there is none rather than said, unlike the cost: no date is the ordinary case.
              */}
              {lot.snapshot?.bestBefore ? (
                <Text component="span" size="xs" c="dimmed">
                  {" · "}
                  {t("lots.bestBefore", {
                    date: formatBestBefore(lot.snapshot.bestBefore),
                  })}
                </Text>
              ) : null}
            </Text>
            {/*
              `R-LOT-33` — *X left* in the room, for a fixed-price lot. A switch on the row rather
              than a field in the form below, because the form locks at the feature and this does
              not: a host watching a lot sell slowly may want the count shown mid-lot. Saved as it
              is flipped, and the room hears it with the lot's next state.
            */}
            {/*
              `R-LOT-34` — a timed lot's limit before it opens, and its countdown once it has. The
              countdown ticks here rather than waiting for the panel's poll, because a host
              deciding whether to talk the lot up needs the number now, not five seconds ago.
            */}
            {lot.saleMode === "fixed" && lot.state === "selling" && lot.closesAt ? (
              <SaleCountdown closesAt={lot.closesAt} />
            ) : lot.saleMode === "fixed" &&
              lot.sellSeconds !== null &&
              ["queued", "featured"].includes(lot.state) ? (
              <Text size="xs" c="dimmed">
                {t("lots.sellSecondsRow", { seconds: lot.sellSeconds })}
              </Text>
            ) : null}
            {controls &&
            lot.saleMode === "fixed" &&
            ["queued", "featured", "selling"].includes(lot.state) ? (
              <Switch
                size="xs"
                label={t("lots.showRemaining")}
                checked={lot.showRemaining}
                disabled={busy !== undefined}
                onChange={(event) => {
                  const showRemaining = event.currentTarget.checked;
                  void run("showRemaining", () =>
                    shows.editLot(showId, lot.id, { showRemaining }),
                  );
                }}
              />
            ) : null}
            {/* The auction's terms, in the row rather than only in the form below — an operator
                scanning a running order needs to see at a glance which lots extend and which stop
                dead (`R-AUC-9` is the same fact stated for viewers). Reads from the lot, not from
                the form's state, so it says what is saved rather than what is typed. */}
            {lot.saleMode === "auction" && lot.auction ? (
              <Text size="xs" c="dimmed">
                {t("lots.auction.rowMeta", {
                  increment: formatPrice(lot.auction.minIncrement),
                  duration: lot.auction.durationSeconds,
                  mode: t(`lots.auction.mode.${lot.auction.closeMode}`),
                })}
                {lot.auction.closeMode === "standard"
                  ? t("lots.auction.rowMetaCounter", {
                      counter: lot.auction.counterBidSeconds,
                    })
                  : null}
                {/*
                  How far through the lot's units the show is (`R-AUC-28`). Only where there is
                  more than one — a single-unit auction lot has no progress to report, and the
                  line on every one of them would be noise in a running order.

                  **The auction number is not a fraction of the quantity**, though it sits beside
                  one. It counts how many times this lot has been put up, and a lot can be put up
                  more often than it has units: a declined charge gives the unit back (`R-AUC-26`)
                  and a relisted lot starts another (`R-HOST-17`), so a five-unit lot on its sixth
                  auction is the re-offer it looks like rather than a bug. The number is an
                  identity — it is the one in the Redis key and in the closer's log — and
                  `remaining` is the count that answers how much is left to sell.
                */}
                {lot.allocated > 1 ? (
                  <Text component="span" size="xs">
                    {" · "}
                    {t("lots.auction.roundProgress", {
                      round: lot.round,
                      remaining: lot.remaining,
                      allocated: lot.allocated,
                    })}
                  </Text>
                ) : null}
              </Text>
            ) : null}
            {/*
              Sold units held at Shopify until their orders exist (M1.13, D7). Said on the lot so
              that a figure in the shop's inventory history has an explanation where somebody
              looking at the lot will look.
            */}
            {lot.reserved > 0 ? (
              <Text size="xs" c="dimmed">
                {t("lots.held", { count: lot.reserved })}
              </Text>
            ) : null}
            {/* The show price differs from Shopify's, which the snapshot kept so the difference
                stays visible rather than being discovered on an invoice (spec §7.2). */}
            {lot.snapshot &&
            !lot.priceFollowsShopify &&
            lot.snapshot.shopifyPrice !== lot.price ? (
              <Text size="xs" c="dimmed">
                {t("lots.overridden", {
                  shopify: formatPrice(lot.snapshot.shopifyPrice),
                })}
              </Text>
            ) : null}
          </Stack>

          {/* Not rendered at all for a moderator, rather than rendered with every branch false:
              withdraw and relist are offered on lot state alone, so a `running` of false would
              have left two of the row's most consequential buttons in place. */}
          {controls ? (
            <LotActions
              state={lot.state}
              anotherOpen={anotherOpen}
              running={running}
              busy={busy}
              onAct={(action) =>
                void run(action, () => shows.actOnLot(showId, lot.id, action))
              }
              onRunAgain={() =>
                void run("again", () =>
                  /*
                    The same product, featured again as a **new lot** with this one's settings
                    (M1.13): the price it was offered at, its mode, its auction terms, and a
                    fixed-price lot's time limit and *X left*. An
                    auction offers one unit; a fixed-price lot takes the default share of what
                    Shopify has now. Stock is checked exactly as for any feature.
                  */
                  shows.feature(
                    showId,
                    { variantId: lot.variantId },
                    {
                      saleMode: lot.saleMode,
                      price: lot.price,
                      ...(lot.saleMode === "auction"
                        ? {
                            quantity: 1,
                            ...(lot.auction ? { auction: lot.auction } : {}),
                          }
                        : {
                            /*
                              A fixed-price lot's own settings come with it: its time limit
                              (`R-LOT-34`) and whether the room shows how many are left
                              (`R-LOT-33`). Left out, the new lot went on air untimed.
                            */
                            sellSeconds: lot.sellSeconds,
                            showRemaining: lot.showRemaining,
                          }),
                    },
                  ),
                )
              }
            />
          ) : null}
        </Group>

        {editable && showState !== "ended" && showState !== "cancelled" ? (
          <>
            <Group gap="xs" align="flex-end" wrap="wrap">
              {/* The sale mode, while the lot is still queued. Not offered from `featured`
                onwards: `R-AUC-8` lets the auction's *settings* change until the lot opens, and
                turning a featured fixed-price lot into an auction is a different act — the room
                has been showing a claim button for something that would suddenly need bids. */}
              <Stack gap={2}>
                <Text size="xs" fw={500}>
                  {t("lots.modeLabel")}
                </Text>
                <SegmentedControl
                  size="xs"
                  value={saleMode}
                  onChange={(value) => setSaleMode(value as Lot["saleMode"])}
                  disabled={working}
                  data={[
                    { value: "fixed", label: t("lots.mode.fixed") },
                    { value: "auction", label: t("lots.mode.auction") },
                  ]}
                />
              </Stack>
              <TextInput
                size="xs"
                label={t(
                  saleMode === "auction"
                    ? "lots.startingPriceLabel"
                    : "lots.priceLabel",
                )}
                value={price}
                onChange={(event) => setPrice(event.currentTarget.value)}
                disabled={working}
                error={problem !== undefined}
              />
              {/*
              Fixed price only. An auction offers one unit (M1.13) — selling another is *Run
              again* — so it has no quantity to set. A fixed-price lot's quantity is what featuring
              will offer, checked against Shopify's stock then and refused if there is not enough.
              The floor is what has already sold, and one.
            */}
              {saleMode === "fixed" ? (
                <NumberInput
                  size="xs"
                  label={t("lots.quantityLabel")}
                  value={allocated}
                  min={Math.max(lot.consumed, 1)}
                  allowDecimal={false}
                  onChange={(value) => setAllocated(Number(value) || 0)}
                  disabled={working}
                />
              ) : null}
              {saleMode === "fixed" ? (
                <NumberInput
                  size="xs"
                  label={t("lots.sellSecondsLabel")}
                  placeholder={t("lots.sellSecondsNone")}
                  value={sellSeconds}
                  min={SELL_SECONDS_MIN}
                  max={SELL_SECONDS_MAX}
                  suffix="s"
                  allowDecimal={false}
                  allowNegative={false}
                  onChange={(value) =>
                    setSellSeconds(value === "" ? "" : Number(value))
                  }
                  error={sellInvalid}
                  disabled={working}
                />
              ) : null}
              <Button
                size="compact-xs"
                variant="default"
                loading={busy === "save"}
                disabled={
                  working ||
                  problem !== undefined ||
                  (saleMode === "fixed" && sellInvalid) ||
                  (price === lot.price &&
                    allocated === lot.allocated &&
                    !(saleMode === "fixed" && sellChanged) &&
                    saleMode === lot.saleMode &&
                    !(saleMode === "auction" && changed))
                }
                onClick={() =>
                  void run("save", () =>
                    shows.editLot(showId, lot.id, {
                      ...(price !== lot.price ? { price } : {}),
                      // Switched to an auction: one unit, whatever the fixed-price quantity was.
                      ...(saleMode === "auction"
                        ? lot.allocated !== 1 && lot.consumed === 0
                          ? { allocated: 1 }
                          : {}
                        : allocated !== lot.allocated
                          ? { allocated }
                          : {}),
                      ...(saleMode !== lot.saleMode ? { saleMode } : {}),
                      ...(saleMode === "auction" ? { auction } : {}),
                      ...(saleMode === "fixed" && sellChanged
                        ? { sellSeconds: sellSecondsOf(sellSeconds) }
                        : {}),
                    }),
                  )
                }
              >
                {t("lots.save")}
              </Button>
              {/* A shortlisted lot holds nothing (M1.13), so it can simply be removed — unless it has
                already sold (a multi-unit auction between rounds), whose record has to stay. */}
              {lot.reserved === 0 && lot.consumed === 0 ? (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  loading={busy === "remove"}
                  disabled={working}
                  onClick={() =>
                    void run("remove", () => shows.removeLot(showId, lot.id))
                  }
                >
                  {t("lots.remove")}
                </Button>
              ) : null}
              {["draft", "scheduled"].includes(showState) ? (
                // `R-CAT-3` — taking a newer copy of the product is a host action before the show.
                <Button
                  size="compact-xs"
                  variant="subtle"
                  loading={busy === "refresh"}
                  disabled={working}
                  onClick={() =>
                    void run("refresh", () => shows.actOnLot(showId, lot.id, "refresh"))
                  }
                >
                  {t("lots.refresh")}
                </Button>
              ) : null}
            </Group>
            {/*
            The field's own message would sit under it inside this bottom-aligned row and push the
            field up out of line with its neighbours. So the field only turns red, and the reason is
            said once, here, under the whole row.
          */}
            {problem || (saleMode === "fixed" && sellInvalid) ? (
              <Text size="xs" c="red" mt={4}>
                {problem
                  ? t(`lots.priceProblem.${problem}`)
                  : t("lots.sellSecondsInvalid")}
              </Text>
            ) : null}
          </>
        ) : null}

        {/*
          Below the edit group and outside it, because it outlives it: the price and position
          inputs disappear at `featured` and these do not (`R-AUC-8`). On a queued lot the two
          blocks are saved by the one button above; on a featured lot this block carries its own,
          since there is nothing else left on the row to press.
        */}
        {auctionEditable ? (
          <Stack gap="xs" className={styles.card}>
            <AuctionFields value={auction} onChange={setAuction} disabled={working} />
            {!editable ? (
              <Group gap="xs">
                <Button
                  size="compact-xs"
                  variant="default"
                  loading={busy === "auction"}
                  disabled={working || !changed}
                  onClick={() =>
                    void run("auction", () =>
                      shows.editLot(showId, lot.id, { auction }),
                    )
                  }
                >
                  {t("lots.auction.save")}
                </Button>
                <Text size="xs" c="dimmed">
                  {t("lots.auction.untilOpen")}
                </Text>
              </Group>
            ) : null}
          </Stack>
        ) : null}

        {error ? (
          <Text size="xs" c="red">
            {error}
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
}

const LOT_COLOUR: Record<LotState, string> = {
  queued: "gray",
  featured: "blue",
  selling: "green",
  sold: "ink",
  unsold: "orange",
  withdrawn: "gray",
  settled: "ink",
};

function LotStateBadge({ state }: { state: LotState }) {
  const { t } = useTranslation();
  return (
    <Badge size="xs" variant="light" color={LOT_COLOUR[state]} radius="sm">
      {t(`lots.state.${state}`)}
    </Badge>
  );
}

/**
 * The one action that comes next: feature, open, close — and relist for a lot that ended unsold.
 *
 * ## No Skip and no Withdraw (M1.13)
 *
 * They belonged to a running order a show had to get through. There is none now: a shortlisted lot
 * that is not wanted is **removed** — it holds nothing, so nothing is lost — and a lot on air is
 * **closed**, which ends the offer and keeps whatever sold.
 *
 * ## Exactly one action spins
 *
 * `busy` names the action in flight rather than reporting that one exists, so pressing *Open*
 * does not put a spinner in every button on the row. See the note on the state in `LotRow`.
 */
function LotActions({
  state,
  anotherOpen,
  running,
  busy,
  onAct,
  onRunAgain,
}: {
  state: LotState;
  anotherOpen: boolean;
  running: boolean;
  /** The action in flight, or `undefined`. */
  busy: string | undefined;
  onAct: (action: LotAction) => void;
  /** Feature the same product again as a new lot, with this lot's settings (M1.13). */
  onRunAgain: () => void;
}) {
  const { t } = useTranslation();
  const working = busy !== undefined;

  return (
    <Group gap={4} wrap="nowrap">
      {/*
        One featured lot at a time (`R-HOST-15`, `featureLot`). Featuring this one swaps out a lot
        that is featured and not yet open, so that needs no warning; a lot that is **open** has to
        be closed or withdrawn first, and the button says why rather than failing when pressed.
      */}
      {running && state === "queued" ? (
        <Tooltip label={t("lots.featureBlocked")} withArrow disabled={!anotherOpen}>
          {/* A wrapper, because a disabled button fires no pointer events for the tooltip. */}
          <span>
            <Button
              size="compact-xs"
              loading={busy === "feature"}
              disabled={working || anotherOpen}
              onClick={() => onAct("feature")}
            >
              {t("lots.feature")}
            </Button>
          </span>
        </Tooltip>
      ) : null}
      {running && state === "featured" ? (
        <Button
          size="compact-xs"
          loading={busy === "open"}
          disabled={working}
          onClick={() => onAct("open")}
        >
          {t("lots.open")}
        </Button>
      ) : null}
      {running && ["featured", "selling"].includes(state) ? (
        <Button
          size="compact-xs"
          variant="default"
          loading={busy === "close"}
          disabled={working}
          onClick={() => onAct("close")}
        >
          {t("lots.close")}
        </Button>
      ) : null}
      {/*
        No *Skip* and no *Withdraw* since M1.13. A shortlisted lot that is not wanted is simply
        **removed** (the edit row's button — it holds nothing), and a lot on air is **closed**. The
        route still accepts `withdraw`, because lots withdrawn before M1.13 exist, and *Relist*
        below brings them back.
      */}
      {/*
        *Run again* — the way to sell another of something (M1.13). An auction sells one unit, so
        the next one is the same product featured again; a fixed-price lot that sold out and is
        still wanted is the same. One press, with this lot's price, mode and auction terms, and
        the stock checked as for any feature. Only while the show is live, and not while another
        lot is open (`R-HOST-15`).
      */}
      {running && ["sold", "unsold", "withdrawn", "settled"].includes(state) ? (
        <Button
          size="compact-xs"
          loading={busy === "again"}
          disabled={working || anotherOpen}
          onClick={onRunAgain}
        >
          {t("lots.runAgain")}
        </Button>
      ) : null}
      {/* `R-HOST-17` — a lot that ended unsold goes back into the queue in one action. */}
      {["unsold", "withdrawn"].includes(state) ? (
        <Button
          size="compact-xs"
          variant="default"
          loading={busy === "relist"}
          disabled={working}
          onClick={() => onAct("relist")}
        >
          {t("lots.relist")}
        </Button>
      ) : null}
    </Group>
  );
}

/** `R-LOT-34` — *Closes in 1:23*, ticking, for a timed fixed-price lot that is selling. */
function SaleCountdown({ closesAt }: { closesAt: string }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const left = secondsUntil(closesAt, now);
  return (
    <Text size="xs" fw={700} c={left <= 10 ? "red" : "dimmed"}>
      {left > 0
        ? t("lots.closesIn", {
            time: `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`,
          })
        : t("lots.closing")}
    </Text>
  );
}
